import { DASHED, DASHED_THICKNESS, generatedTextures } from '@/app/gridGL/generateTextures';
import { intersects } from '@/app/gridGL/helpers/intersects';
import { getRangeScreenRectangleFromCellRefRange } from '@/app/gridGL/helpers/selection';
import { pixiApp } from '@/app/gridGL/pixiApp/PixiApp';
import { CURSOR_THICKNESS, FILL_SELECTION_ALPHA } from '@/app/gridGL/UI/Cursor';
import type { RefRangeBounds } from '@/app/quadratic-core-types';
import type { Graphics } from 'pixi.js';

export function drawDashedRectangle(options: {
  g: Graphics;
  color: number;
  isSelected: boolean;
  range: RefRangeBounds;
}) {
  const { g, color, isSelected, range } = options;

  const selectionRect = getRangeScreenRectangleFromCellRefRange(range);
  const bounds = pixiApp.viewport.getVisibleBounds();
  if (!intersects.rectangleRectangle(selectionRect, bounds)) {
    return;
  }

  const boundedRight = Math.min(selectionRect.right, bounds.right);
  const boundedBottom = Math.min(selectionRect.bottom, bounds.bottom);

  g.lineStyle({
    width: CURSOR_THICKNESS,
    color,
    alignment: 0.5,
    texture: generatedTextures.dashedHorizontal,
  });
  g.moveTo(selectionRect.left, selectionRect.top);
  g.lineTo(boundedRight, selectionRect.top);
  if (selectionRect.bottom <= bounds.bottom) {
    g.moveTo(boundedRight, selectionRect.bottom);
    g.lineTo(selectionRect.left, selectionRect.bottom);
  }

  g.lineStyle({
    width: CURSOR_THICKNESS,
    color,
    alignment: 0.5,
    texture: generatedTextures.dashedVertical,
  });
  g.moveTo(selectionRect.left, boundedBottom);
  g.lineTo(selectionRect.left, selectionRect.top);
  if (selectionRect.right <= bounds.right) {
    g.moveTo(selectionRect.right, boundedBottom);
    g.lineTo(selectionRect.right, selectionRect.top);
  }

  if (isSelected) {
    g.lineStyle({
      alignment: 0,
    });
    g.moveTo(selectionRect.left, selectionRect.top);
    g.beginFill(color, FILL_SELECTION_ALPHA);
    g.drawRect(
      selectionRect.left,
      selectionRect.top,
      boundedRight - selectionRect.left,
      boundedBottom - selectionRect.top
    );
    g.endFill();
  }
}

export type Offsets = {
  left: number;
  top: number;
  right: number;
  bottom: number;
};

export function drawDashedRectangleMarching(options: {
  g: Graphics;
  color: number;
  march: number;
  noFill?: boolean;
  alpha?: number;
  offsets?: Offsets;
  range: RefRangeBounds;
}): boolean {
  const { g, color, march, noFill, alpha = 1, offsets = { left: 0, top: 0, right: 0, bottom: 0 }, range } = options;
  const selectionRect = getRangeScreenRectangleFromCellRefRange(range);
  const bounds = pixiApp.viewport.getVisibleBounds();
  if (!intersects.rectangleRectangle(selectionRect, bounds)) {
    return false;
  }

  const minX = selectionRect.left + offsets.left;
  const minY = selectionRect.top + offsets.top;
  const maxX = selectionRect.right - offsets.right;
  const maxY = selectionRect.bottom - offsets.bottom;

  const boundedRight = Math.min(maxX, bounds.right);
  const boundedBottom = Math.min(maxY, bounds.bottom);

  if (!noFill) {
    g.clear();
  }

  g.lineStyle({
    alignment: 0.5,
  });
  if (!noFill) {
    g.beginFill(color, alpha);
    g.drawRect(minX, minY, boundedRight - minX, boundedBottom - minY);
    g.endFill();
  }

  g.moveTo(minX, minY);
  g.lineStyle({
    width: CURSOR_THICKNESS,
    color,
    alignment: 0.5,
  });

  const clamp = (n: number, min: number, max: number): number => {
    return Math.min(Math.max(n, min), max);
  };

  // For very large selections, the original loops below would iterate over the entire selection
  // (potentially millions of dashes) even when most of them lie outside the viewport. To avoid
  // that, we advance each loop's starting position into the visible region while preserving the
  // march-phase (i.e., the result is still of the form `start + k * DASHED`), and we tighten the
  // loop's end so we don't iterate past the visible region either.

  // Smallest value of `start + k * DASHED` (k >= 0) that is >= threshold.
  const skipForwardTo = (start: number, threshold: number): number => {
    if (start >= threshold) return start;
    return start + Math.ceil((threshold - start) / DASHED) * DASHED;
  };

  // Largest value of `start - k * DASHED` (k >= 0) that is <= threshold.
  const skipBackwardTo = (start: number, threshold: number): number => {
    if (start <= threshold) return start;
    return start - Math.ceil((start - threshold) / DASHED) * DASHED;
  };

  // This is a bit hacky of an algorithm to ensure the corners are squared and
  // never show less than DASHED_THICKNESS in size. The if statements are to
  // remove lines that are less than the DASHED_THICKNESS.

  let wrapAmount = 0;

  // draw top line
  {
    const topStart = skipForwardTo(minX + march, bounds.left - DASHED / 2);
    const topEnd = boundedRight - DASHED / 2;
    for (let x = topStart; x <= topEnd; x += DASHED) {
      g.moveTo(clamp(x, minX, boundedRight), minY);
      g.lineTo(clamp(x + DASHED / 2, minX, boundedRight), minY);
      wrapAmount = x - topEnd;
    }
    // If the visible portion of the top line is empty, compute what wrapAmount would have been
    // had the unclipped loop run, so the right edge's march phase still lines up.
    if (topStart > topEnd && minX + march <= topEnd) {
      const k = Math.floor((topEnd - (minX + march)) / DASHED);
      wrapAmount = minX + march + k * DASHED - topEnd;
    }
  }

  if (maxX <= boundedRight) {
    // draw right line
    const rightStart = skipForwardTo(minY + wrapAmount, bounds.top - DASHED / 2);
    const rightEnd = boundedBottom - DASHED / 2;
    for (let y = rightStart; y <= rightEnd; y += DASHED) {
      if (y + DASHED / 2 > minY + DASHED_THICKNESS) {
        g.moveTo(boundedRight, clamp(y, minY, boundedBottom));
        g.lineTo(boundedRight, clamp(y + DASHED / 2, minY, boundedBottom));
        wrapAmount = y + DASHED / 2 - boundedBottom;
      }
    }
    if (rightStart > rightEnd && minY + wrapAmount <= rightEnd) {
      const k = Math.floor((rightEnd - (minY + wrapAmount)) / DASHED);
      wrapAmount = minY + wrapAmount + k * DASHED + DASHED / 2 - boundedBottom;
    }
  }

  if (maxY <= boundedBottom) {
    // draw bottom line
    // The bottom-edge dashes only become visible at x <= boundedRight + DASHED/2 (anything
    // higher is clamped to a zero-length line at boundedRight). Using `boundedRight` rather than
    // `bounds.right` matters when the rect's right edge is inside the viewport AND wrapAmount is
    // very negative (huge selection extending left), so the loop starts in the rect's range.
    const bottomStart = skipBackwardTo(boundedRight - wrapAmount, boundedRight + DASHED / 2);
    const bottomEnd = Math.max(minX + DASHED / 2, bounds.left - DASHED / 2);
    for (let x = bottomStart; x >= bottomEnd; x -= DASHED) {
      if (x - DASHED / 2 < boundedRight - DASHED_THICKNESS) {
        g.moveTo(clamp(x - DASHED / 2, minX, boundedRight - DASHED_THICKNESS), boundedBottom - DASHED_THICKNESS);
        g.lineTo(clamp(x, minX, boundedRight), boundedBottom - DASHED_THICKNESS);
      }
      wrapAmount = minX - x - DASHED / 2;
    }
  }

  // draw left line
  // When the bottom-edge loop was clipped (bottomEnd raised above `minX + DASHED/2`), wrapAmount
  // here differs from what the unclipped algorithm would have produced — but only by a multiple
  // of DASHED, because every loop iteration shifts wrapAmount by exactly DASHED. So as long as
  // `skipBackwardTo` brings leftStart down to a tight, single-DASHED window above boundedBottom,
  // both wrapAmounts land on the *same* visible starting position. Using `boundedBottom` rather
  // than `bounds.bottom` is what guarantees that.
  {
    const leftStart = skipBackwardTo(boundedBottom - wrapAmount, boundedBottom + DASHED / 2);
    const leftEnd = Math.max(minY + DASHED / 2, bounds.top - DASHED / 2);
    for (let y = leftStart; y >= leftEnd; y -= DASHED) {
      g.moveTo(minX + DASHED_THICKNESS, clamp(y - DASHED / 2, minY, boundedBottom));
      g.lineTo(minX + DASHED_THICKNESS, clamp(y, minY, boundedBottom));
    }
  }

  return true;
}
